import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useStripePaymentRedirectResult } from "@/hooks/use-stripe-payment-redirect-result";
import { useCompleteBillingSetup, useOrganization } from "@/queries/organization-queries";
import { Plan } from "@/routes/settings/organization/billing/Plan";
import { CreditBalance } from "@/routes/settings/organization/billing/CreditBalance";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useEffect, type FC } from "react";
import { StripePortalCard } from "@/routes/settings/organization/billing/StripePortalCard";
import { Banner } from "@/components/flexkit/Banner";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

const BillingPage: FC = () => {
    useDocumentTitle("Billing");

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();
    const { redirectResult, clearStripePaymentRedirectParams } = useStripePaymentRedirectResult();
    const completeBillingSetup = useCompleteBillingSetup();
    const { toast } = useToast();

    useEffect(() => {
        if (!redirectResult) {
            return;
        }

        if (redirectResult.redirectStatus === "succeeded") {
            // Complete the billing setup with the payment intent ID
            completeBillingSetup.mutate(redirectResult.paymentIntent, {
                onSuccess: () => {
                    toast({
                        title: "Billing setup completed",
                        description: "Your billing has been successfully set up.",
                    });
                    void clearStripePaymentRedirectParams();
                },
                onError: (error) => {
                    toast({
                        title: "Failed to complete billing setup",
                        description: formatError(error),
                    });
                    void clearStripePaymentRedirectParams();
                },
            });
        } else if (redirectResult.redirectStatus === "canceled") {
            toast({
                title: "Billing setup cancelled",
                description: "The billing setup was cancelled. Please try again.",
            });
            void clearStripePaymentRedirectParams();
        } else if (redirectResult.redirectStatus) {
            toast({
                title: "Billing setup incomplete",
                description: "The billing setup could not be completed. Please try again.",
            });
            void clearStripePaymentRedirectParams();
        }
    }, [redirectResult, completeBillingSetup, toast, clearStripePaymentRedirectParams]);

    if (!organization && isLoadingOrganization) {
        return <LoadingState />;
    }

    if (!membership && isLoadingMembership) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (organization?.tier === OrganizationTier.ENTERPRISE) {
        return (
            <Banner
                variant="info"
                data-testid="enterprise-tier-banner"
                className="mt-6 max-w-[550px]"
                text={
                    <div className="pl-2">
                        <Text>Billing management is not available for Enterprise tier organizations.</Text>
                        <Text>
                            To make changes to your plan or billing, please contact your account representative.
                        </Text>
                    </div>
                }
            />
        );
    }

    return (
        <div className="flex max-w-[820px] flex-col gap-6">
            <div className="text-base text-content-secondary">Manage usage and billing details</div>
            <Plan />
            <CreditBalance />
            <StripePortalCard />
        </div>
    );
};

export default BillingPage;
